import { useRouter } from 'next/router';
import { JoinViewModel } from './JoinViewModel';
import { TextLGRegular, TextSMMedium, TextSMRegular, TextXSRegular } from 'styleguide/Texts';
import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { View } from 'styleguide/View';
import { CardNoPadding } from 'styleguide/Card';
import { Spacing } from 'styleguide/spacing';
import { ProfileImage } from 'components/ProfileImage';
import { colors } from 'styleguide/colors';
import { FormInput, StyledCheckbox, TextAreaAutosize } from 'styleguide/Inputs';
import { PrimaryButton } from 'styleguide/Buttons';
import { GenericErrorScreen } from 'components/GenericErrorScreen';
import { CustomizableIcon } from 'styleguide/CustomizableIcon';
import Link from 'next/link';

export const JoinScreen = () => {
  const router = useRouter();
  const coachUsername = router.query.coachUsername as string;
  const isPreview = router.query.preview === 'true';
  const previewMessage = router.query.message as string;
  const previewImage = router.query.image as string;
  const previewCoachName = router.query.coachName as string;
  const previewCoachProfilePic = router.query.profileImage as string;
  if (!coachUsername) {
    return <GenericErrorScreen error="Invalid coach username." />;
  }
  const vm = new JoinViewModel(
    coachUsername,
    isPreview,
    previewImage,
    previewMessage,
    previewCoachName,
    previewCoachProfilePic,
  );

  return <JoinScreenInner vm={vm} />;
};

const Container = styled(View)`
  max-width: 740px;
  align-items: center;
  align-self: center;
  margin: ${Spacing.md}px;
`;

const SuccessView = () => {
  return (
    <View
      style={{
        textAlign: 'center',
        alignItems: 'center',
        flex: 1,
        margin: Spacing.lg,
        gap: Spacing.md,
      }}
    >
      <CustomizableIcon type="check-circle" tint={colors.primary500} width={75} height={75} />
      <TextSMRegular>
        Your request has been submitted. You will receive an email when the coach accepts or
        declines your request.
      </TextSMRegular>
    </View>
  );
};

const LoadFailedView = () => {
  return (
    <View
      style={{
        textAlign: 'center',
        alignItems: 'center',
        flex: 1,
        marginLeft: Spacing.md,
        marginRight: Spacing.md,
        marginTop: Spacing.md,
        marginBottom: Spacing.lg,
      }}
    >
      <View
        style={{
          textAlign: 'center',
          alignItems: 'center',
          flex: 1,
          margin: Spacing.lg,
          gap: Spacing.md,
        }}
      >
        <CustomizableIcon type="warning-triangle" tint={colors.primary500} width={75} height={75} />
        <TextSMRegular>
          Unable to load the form. Please double-check the link or try again later.
        </TextSMRegular>
      </View>
    </View>
  );
};

const LeadForm = observer(({ vm }: { vm: JoinViewModel }) => {
  return (
    <View
      style={{
        textAlign: 'center',
        alignItems: 'center',
        flex: 1,
        marginLeft: Spacing.md,
        marginRight: Spacing.md,
        marginTop: Spacing.md,
        marginBottom: Spacing.lg,
      }}
    >
      <View
        style={{
          textAlign: 'left',
          maxWidth: 450,
          width: '100%',
          gap: Spacing.sm,
        }}
      >
        <TextSMMedium>Full Name</TextSMMedium>
        <FormInput
          value={vm.leadFullname}
          onChange={e => {
            vm.onLeadFullnameChange(e.target.value);
          }}
        ></FormInput>
        <TextSMMedium style={{ paddingTop: Spacing.sm }}>Email</TextSMMedium>

        <FormInput
          value={vm.leadEmail}
          onChange={e => {
            vm.onLeadEmailChange(e.target.value);
          }}
        ></FormInput>
        {vm.showEmailError && (
          <TextSMRegular style={{ color: colors.error500 }}>
            Please enter a valid email address.
          </TextSMRegular>
        )}
        <TextSMMedium style={{ paddingTop: Spacing.sm }}>Message</TextSMMedium>
        <TextAreaAutosize
          value={vm.leadMessage}
          minRows={4}
          onChange={e => {
            vm.onLeadMessageChange(e.target.value);
          }}
        ></TextAreaAutosize>
        <StyledCheckbox
          label={
            <TextSMRegular style={{ paddingLeft: Spacing.sm, marginTop: -2 }}>
              I have read and I accept the{' '}
              <a
                href="https://hevycoach.com/terms-and-conditions/"
                target="_blank"
                rel="noreferrer"
              >
                terms and conditions
              </a>
            </TextSMRegular>
          }
          style={{ marginBottom: Spacing.md }}
          checked={vm.hasAcceptedTerms}
          onChange={event => vm.onUpdateTerms(event.currentTarget.checked)}
        />
        <PrimaryButton
          title="Send"
          onClick={vm.onSendClick}
          loading={vm.isLoading}
          enabled={vm.isSendEnabled}
        />
      </View>
    </View>
  );
});

const FormHeader = observer(
  ({
    coachMessage,
    coachName,
    coachProfileImage,
    bannerImageUrl,
  }: {
    coachMessage: string;
    coachProfileImage?: string;
    coachName: string;
    bannerImageUrl?: string;
  }) => {
    return (
      <>
        {!!bannerImageUrl ? (
          <img
            src={bannerImageUrl}
            alt="Join Image"
            style={{ aspectRatio: 740 / 180, height: 'auto', width: '100%' }}
            width={100}
            height={100}
          />
        ) : (
          // Placeholder
          <View
            style={{
              aspectRatio: 740 / 180,
              height: 'auto',
              width: '100%',
              backgroundColor: colors.neutral300,
            }}
          />
        )}
        <View
          style={{
            // Make the coach's profile image overlap a bit
            marginTop: -30,
            textAlign: 'center',
            alignItems: 'center',
            flex: 1,
            marginLeft: Spacing.md,
            marginRight: Spacing.md,
          }}
        >
          <ProfileImage
            style={{ borderColor: colors.white, borderStyle: 'solid', borderWidth: 2 }}
            source={coachProfileImage}
            diameter={80}
          />
          <TextLGRegular
            style={{ color: 'black', maxWidth: 450, width: '100%', marginBottom: Spacing.sm }}
          >
            {coachMessage}
          </TextLGRegular>
          <TextSMRegular>{coachName}</TextSMRegular>
        </View>
      </>
    );
  },
);

const JoinScreenInner = observer(({ vm }: { vm: JoinViewModel }) => {
  if (!vm.joinMetadata && !vm.isLoading) {
    return <LoadFailedView />;
  }
  return (
    <Container>
      <CardNoPadding
        style={{
          overflow: 'hidden',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <FormHeader
          coachMessage={vm.coachMessage}
          coachProfileImage={vm.joinMetadata?.coachProfileImageUrl}
          coachName={vm.coachName}
          bannerImageUrl={vm.joinMetadata?.joinImageUrl}
        />
        {vm.didSuccessfullySubmit ? <SuccessView /> : <LeadForm vm={vm} />}
      </CardNoPadding>
      <Link style={{ textDecoration: 'none' }} target="_blank" href="https://hevycoach.com">
        <TextXSRegular>Created with Hevy Coach</TextXSRegular>
      </Link>
    </Container>
  );
});
